<script>
import { find } from 'lodash';
import { mapState, mapGetters } from 'vuex';
import {
  isEmpty,
  consts,
  serverError,
  mobileCheck,
} from 'src/utils';
import SnackbarError from 'src/shared/form/SnackbarError';

let tagInputFields = ['text-field', 'checkbox', 'select'];
const tagActions = ['btn'];

function inputFields(vm) {
  return vm.$data.form.inputFields;
}

function submitFields(vm) {
  return vm.$data.form.submitFields;
}

function requiredFieldText(fieldName) {
  return serverError.interpolationWord('%s cant be blank', fieldName);
}

function getFields(vnodes, partComponentTags) {
  if (vnodes === undefined) {
    return [];
  }

  let submittingNodes = vnodes
    .filter((x) => x.tag)
    .filter((x) => !!find(partComponentTags, (y) => x.tag.indexOf(y) > -1));

  vnodes.forEach((node) => {
    submittingNodes = submittingNodes.concat(getFields(node.children, partComponentTags));
  });

  return submittingNodes;
}

function getErrorText(error, name) {
  return error && !isEmpty(name) ? requiredFieldText(name) : error;
}

async function onSubmit(vm, submitCallback) {
  try {
    vm.isDisabled = true;
    const validationResult = vm.validate();
    if (validationResult && submitCallback) {
      await submitCallback();
    } else if (validationResult) {
      vm.$emit('submit');
    } else {
      vm.isDisabled = false;
    }
  } catch (err) {
    if (err.graphQLErrors) {
      err.graphQLErrors.forEach(({ error, message }) => {
        if (error && error.code === consts.SYSTEM_EXCEPTION) {
          vm.error.text = vm.$t(`T_RESPONSE_${consts.SYSTEM_EXCEPTION}`);
        } else if (error && error.code === consts.RECORD_NOT_FOUND) {
          vm.error.text = vm.$t(`T_RESPONSE_${consts.RECORD_NOT_FOUND}`);
        } else if (message) {
          vm.error.text = vm.$t(message);
        } else vm.error.text = vm.$t('T_ERROR');
      });
    } else {
      vm.error.text = err;
    }
    vm.error.show = true;
    vm.$emit('on-error', err);
  } finally {
    if (submitCallback) {
      vm.isDisabled = false;
    }
  }
}

export default {
  name: 'vc-form',
  props: {
    hidden: {
      type: Boolean,
      default: false,
    },
    customInputFields: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    SnackbarError,
  },
  data() {
    return {
      rules: {
        required: (value) => ((value == null || isEmpty(String(value)))
          ? serverError.interpolationWord('%s cant be blank', 'T_FIELD') : true),
      },
      error: {
        text: '',
        show: false,
      },
      isDisabled: false,
      form: {
        inputFields: [],
        submitFields: [],
      },
      isMobileBrowser: false,
    };
  },
  beforeMount() {
    tagInputFields = tagInputFields.concat(this.customInputFields);

    this.$data.form.inputFields = getFields(this.$slots.default, tagInputFields);

    if (inputFields(this).length > 0
      && !find(inputFields(this), (x) => x.componentOptions.propsData.autofocus !== undefined)) {
      inputFields(this)[0].componentOptions.propsData.autofocus = '';
    }
  },
  mounted() {
    const vm = this;

    this.isMobileBrowser = mobileCheck();
    inputFields(vm).forEach((field) => {
      const vnodes = field.componentInstance.$children.filter((x) => x.$vnode);
      this.$data.form.inputFields = inputFields(vm).concat(getFields(vnodes, tagInputFields));
    });

    inputFields(vm).forEach((field) => {
      if (field.componentOptions.propsData.required !== undefined) {
        field.componentInstance.rules[field.componentInstance.rules.length] = vm.rules.required;
      }
      field.elm.onkeyup = (event) => {
        if (event.keyCode === 13 && event.target.type !== 'textarea') {
          onSubmit(vm);
        }
      };
    });

    this.$data.form.submitFields = getFields(this.$slots.default, tagActions)
      .filter((x) => x.data.attrs.sending !== undefined);

    submitFields(vm).forEach(({ elm }) => {
      elm.onclick = () => {
        onSubmit(vm);
      };
    });
  },
  computed: {
    ...mapState('demo', ['page']),
    ...mapGetters('app', ['isUcEnv']),
    isMobileUc() {
      return this.isMobileBrowser && this.isUcEnv;
    },
    isVoteUcMobilePage() { // для отображения в мобильных браузерах в теме UC
    // пока меняем высоту обертки только для страницы vote - в дальнейшем
    // может потребоваться и для других страниц -
    // например, логина, etc (пока они не используются в UC)
      return this.isMobileUc && this.page === 'vote';
    },
  },
  methods: {
    validate() {
      const vm = this;
      let result = true;

      inputFields(vm).forEach((vnode) => {
        const {
          componentOptions: { propsData: { name } },
          data: { attrs: { name: _name } },
          componentInstance: { validate, validations },
        } = vnode;

        if (validate === undefined) { return !result; }

        validate();

        if (validations.length > 0) {
          result = false;
          vm.error.show = true;
          vm.error.text = getErrorText(validations[0], isEmpty(name) ? _name : name);
        }
        return !result;
      });

      return result;
    },
    async withErrorHandling(submitCallback) {
      if (typeof submitCallback === 'function') {
        await onSubmit(this, submitCallback);
      }
    },
  },
};
</script>

<template lang="pug">
.vc-form(:class="isVoteUcMobilePage && 'vc-formMobile'")
  .progress-wrapper(v-show="isDisabled")
    v-progress-circular.progress-circular.white--text(indeterminate="", :size="50", :width="3")
  slot
  snackbar-error(v-model="error.show", v-if="!hidden", :error="error.text", timeout="8000")
    slot(name="error-close-btn")
</template>

<style lang="sass">
.vc-form
  text-align: left
  overflow: hidden
  &Mobile
    overflow: auto
    height: 100%
.progress-wrapper
  position: absolute
  top: 0
  right: 0
  bottom: 0
  left: 0
  background-color: $color_black
  opacity: 0.2
  z-index: 100
  .progress-circular
    position: absolute
    top: 0
    right: 0
    bottom: 0
    left: 0
    margin: auto
#error-text
  line-height: 1.2
.input-group__error
  font-size: 11px
  font-weight: 400
  line-height: 2.2
.snack--bottom
  bottom: 40px
.snack__content
  height: 40px

</style>
